import CONFIG from '../config/config';
import { instanceWithV2 as APIClient } from './SimpleAPIClient';

export type TimeRangeParams = { startTime: string; endTime: string };

export type SeriesResponse = {
  labels: string[];
  values: number[];
};

/** GET /v1/statistics/meetings/summary/total-over-time — 1st–3rd try-ons in the past slice, presented vs bride-cancelled by day */
export type MeetingsVisitsOverTimeSeriesResponse = {
  labels: string[];
  notBrideCancelledValues: number[];
  brideCancelledValues: number[];
};

export type RatioResponse = {
  numerator: number;
  denominator: number;
  percentage: number; // 0-100
};

export type PastVisitsInsights = {
  total: number;
  presented: number;
  cancelled: number;
  netBrides: number;
};

export type UpcomingVisitsInsights = {
  total: number;
  firstVisit: number;
  secondVisit: number;
  thirdVisit: number;
  fittingsTotal: number;
};

/** GET /v1/statistics/meetings/summary/upcoming-visits — extended payload; `count` is backward-compatible */
export type VisitsInsightsResponse = {
  count: number;
  past: PastVisitsInsights;
  upcoming: UpcomingVisitsInsights;
};

const qs = (params: Record<string, string | number | undefined>) => {
  const sp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null) sp.append(k, String(v));
  });
  const s = sp.toString();
  return s ? `?${s}` : '';
};

const MeetingsStatisticsAPI = {
  getMeetingsTotalOverTime: ({ startTime, endTime }: TimeRangeParams) =>
    APIClient(CONFIG.API_ENDPOINT).get<MeetingsVisitsOverTimeSeriesResponse>(
      `/v1/statistics/meetings/summary/total-over-time${qs({ startTime, endTime })}`
    ),

  getMeetingsTypeDistribution: ({ startTime, endTime }: TimeRangeParams) =>
    APIClient(CONFIG.API_ENDPOINT).get<SeriesResponse>(
      `/v1/statistics/meetings/summary/type-distribution${qs({ startTime, endTime })}`
    ),

  getNoShowsRatio: ({ startTime, endTime }: TimeRangeParams) =>
    APIClient(CONFIG.API_ENDPOINT).get<RatioResponse>(
      `/v1/statistics/meetings/summary/no-shows-ratio${qs({ startTime, endTime })}`
    ),

  getNoShowsOverTime: ({ startTime, endTime }: TimeRangeParams) =>
    APIClient(CONFIG.API_ENDPOINT).get<SeriesResponse>(
      `/v1/statistics/meetings/summary/no-shows-over-time${qs({ startTime, endTime })}`
    ),

  getBookingsTotal: ({ startTime, endTime }: TimeRangeParams) =>
    APIClient(CONFIG.API_ENDPOINT).get<{ total: number }>(
      `/v1/statistics/bookings/summary/total${qs({ startTime, endTime })}`
    ),

  getBookingsTypeDistribution: ({ startTime, endTime }: TimeRangeParams) =>
    APIClient(CONFIG.API_ENDPOINT).get<SeriesResponse>(
      `/v1/statistics/bookings/summary/type-distribution${qs({ startTime, endTime })}`
    ),

  getBookingsCancellationRatio: ({ startTime, endTime }: TimeRangeParams) =>
    APIClient(CONFIG.API_ENDPOINT).get<RatioResponse>(
      `/v1/statistics/bookings/summary/cancellation-ratio${qs({ startTime, endTime })}`
    ),

  getPendingBookingsBrideCancellationRatio: ({ startTime, endTime }: TimeRangeParams) =>
    APIClient(CONFIG.API_ENDPOINT).get<RatioResponse>(
      `/v1/statistics/bookings/summary/pending-bride-cancellation-ratio${qs({ startTime, endTime })}`
    ),

  getBookingsInboundChannels: ({ startTime, endTime }: TimeRangeParams) =>
    APIClient(CONFIG.API_ENDPOINT).get<SeriesResponse>(
      `/v1/statistics/bookings/summary/inbound-channels${qs({ startTime, endTime })}`
    ),

  getBookingsPendingPriceRanges: ({ startTime, endTime }: TimeRangeParams) =>
    APIClient(CONFIG.API_ENDPOINT).get<SeriesResponse>(
      `/v1/statistics/bookings/summary/pending-price-ranges${qs({ startTime, endTime })}`
    ),

  getUpcomingVisits: ({ startTime, endTime }: TimeRangeParams) =>
    APIClient(CONFIG.API_ENDPOINT).get<VisitsInsightsResponse>(
      `/v1/statistics/meetings/summary/upcoming-visits${qs({ startTime, endTime })}`
    ),
};

export default MeetingsStatisticsAPI;


